// src/lib/api.client.ts — consumul formei API {_s:"v", data, ok, err} în browser.
// Singura definiție client a tipurilor de răspuns: derivate (import type, șters la build)
// din lib/safe.ts — sursa de adevăr a formei produse de result_to_api pe server.
// safe.ts e vendorat (nu se editează), iar API_RESULT.ok e `boolean` (nu discriminează),
// de aceea uniunea discriminată trăiește aici.

import type { API_ERROR, API_RESULT } from "./safe"
import { note_failed_api } from "./support_context.client"

export type ApiOk<T> = Omit<API_RESULT<T>, "ok" | "err" | "_s"> & { _s?: "v"; ok: true }
export type ApiErr = Omit<API_RESULT<unknown>, "ok" | "err" | "data" | "_s"> & {
	_s?: "v"
	ok: false
	err?: API_ERROR
}
export type ApiResult<T> = ApiOk<T> | ApiErr

function net_err(e: unknown): ApiErr {
	return { ok: false, err: { code: "network_error", message: String(e) } }
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
	try {
		// credentials: include → trimite cookie-ul de sesiune (autorizarea pe tenant depinde de el).
		const res = await fetch(path, { credentials: "include", ...init })
		if (!res.ok) {
			// contextul tichetelor de suport (F14): ultimele cereri eșuate, fără query string
			note_failed_api(init?.method ?? "GET", path.split("?")[0], res.status)
		}
		return (await res.json()) as ApiResult<T>
	} catch (e) {
		note_failed_api(init?.method ?? "GET", path.split("?")[0], 0)
		return net_err(e)
	}
}

export function api_get<T>(path: string): Promise<ApiResult<T>> {
	return request<T>(path)
}

export function api_post<T>(path: string, body: unknown): Promise<ApiResult<T>> {
	return request<T>(path, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	})
}

export function api_patch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
	return request<T>(path, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	})
}

export function api_delete<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
	return request<T>(path, {
		method: "DELETE",
		headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
		body: body !== undefined ? JSON.stringify(body) : undefined,
	})
}

/** Upload multipart (FormData) — pentru /api/import. */
export function api_upload<T>(path: string, fd: FormData): Promise<ApiResult<T>> {
	return request<T>(path, { method: "POST", body: fd })
}
